/**
 * The answer to a parked document's pre-read question (LAT-982, frames A6/A11–A13).
 *
 * "Delete upload" travels through the same mutation as the three reading answers, because it
 * is one of the answers the question offers — the pre-read popover is the only place a person
 * sees "this is 3,000 pages" and realises they uploaded the wrong file — and routing it here
 * keeps the question's precondition (this file *is* waiting on you) true for every answer.
 */

import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { ReadScope } from "@repo/shared";
import { useCallback } from "react";
import { toast } from "sonner";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import {
  type DocumentAnswerOutcome,
  resolveDocumentRefusalMessage,
} from "../lib/document-processing-view";
import { usePendingArtifactClaims } from "./use-pending-artifact-claims";

/** The four reading answers, plus the one that keeps nothing. */
export type DocumentReadScopeAnswer = ReadScope | { kind: "delete" };

export type UseAnswerReadScopeResult = {
  /**
   * Resolves `{ok:true}` when the answer landed, and carries the refusal's own sentence when
   * it did not — the popover shows it next to the control that has to change, which a toast
   * alone cannot do for "that range is outside this document". A second press while the first
   * answer is still in flight resolves `{ok:false, message:null}`: nothing was sent and there
   * is nothing to say.
   */
  answerReadScope: (input: {
    artifactId: string;
    name: string;
    answer: DocumentReadScopeAnswer;
  }) => Promise<DocumentAnswerOutcome>;
  /** Rows with an answer in flight, so the popover's button can say so. */
  isAnswering: (artifactId: string) => boolean;
};

export function useAnswerReadScope(projectId: Id<"projects">): UseAnswerReadScopeResult {
  const answer = useObservedMutation(api.documentReadScope.answerReadScope, {
    feature: "documents",
    operation: "answer-read-scope",
    includeRequestId: true,
  });
  const { claim, release, isPending } = usePendingArtifactClaims();

  const answerReadScope = useCallback(
    async (input: {
      artifactId: string;
      name: string;
      answer: DocumentReadScopeAnswer;
    }): Promise<DocumentAnswerOutcome> => {
      // Claimed before the round trip, not after it: the popover offers four answers side by
      // side, so the press that beats the re-render is a *different* answer to the same
      // question — and the mutation refuses the second one as no longer awaiting, which reads
      // as the screen having lost the answer the person just gave.
      if (!claim([input.artifactId])) return { ok: false, message: null };
      try {
        const result = await answer({
          projectId,
          artifactId: input.artifactId as Id<"artifacts">,
          answer: input.answer,
        });
        toast.success(
          result.outcome === "deleted" ? `Deleted ${input.name}` : `Reading ${input.name}`
        );
        return { ok: true };
      } catch (error) {
        // Reported twice on purpose: inline, where the correction is made, and in a toast
        // because the one refusal that means "somebody already answered for this document"
        // also takes the row's button — and the panel hanging off it — away.
        const message = resolveDocumentRefusalMessage(
          error,
          `Couldn't start reading ${input.name}`
        );
        toast.error(message);
        return { ok: false, message };
      } finally {
        release([input.artifactId]);
      }
    },
    [answer, claim, projectId, release]
  );

  return { answerReadScope, isAnswering: isPending };
}
